/**
 * Bell icon + dropdown notification center.
 *
 * Tabs: All, Unread, Favorites
 * Date dividers: Today, Yesterday, Older
 * Shows running/completed/failed background jobs with progress indicators.
 *
 * ⛔ VIKTOR, Ads header bell (2026-08-31): pinned Ads rows above the job
 * list are a DIFFERENT thing from Notification-store jobs — persisted DB
 * counts (Management Easy Yes/No, Search Terms), not a running/done/failed
 * lifecycle. They render directly from operatorQueueQuery props, never
 * enter useNotificationStore — no dedupe-across-polls problem, no fake
 * status. "Bell — out of V1" was about a *silent* rollup; a rollup that
 * clicks through to the queue is a different thing (his words).
 *
 * ⛔ VIKTOR round 2: the first version summed ads counts into the primary
 * badge (count + adsSum). Bug — that number is captioned "unread" and
 * Mark all read visibly does nothing to the ads portion, since ads items
 * were never in the notification store to mark read. Two different
 * lifecycles do not get to share one number. Fixed: the primary badge is
 * unread job count ONLY, exactly as before this feature existed. Ads
 * pending gets its own small presence dot (not a number — the actual
 * counts already live in the pinned rows below and the sidebar badges),
 * which persists across Mark all read since it isn't about read state at
 * all — it clears the same way the sidebar badge does: resolve the item.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Bell, Check, ChevronRight, ExternalLink, X, Loader2, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiClient } from "@/services/apiClient";
import {
	useNotificationStore,
	type Notification,
} from "@/stores/useNotificationStore";

/** One row from planner.activity_events, as served by /planner/activity/recent. */
interface ActivityEvent {
	id: string;
	created_at: string;
	actor_email: string | null;
	kind: string;
	entity_type: string | null;
	entity_id: string | null;
	client_id: string | null;
	title: string;
	message: string | null;
	metadata: Record<string, unknown> | null;
}

// Must match navigation.tsx's Ads Management group exactly — no shared
// constant exists yet to import (routeTitles there is keyed by pathname
// only, without the platform query param these need).
const ADS_MANAGEMENT_URL = "/ads-tools/management?platform=google-ads";
const ADS_SEARCH_TERMS_URL = "/ads-tools/search-terms?platform=google-ads";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Tab = "all" | "unread" | "favorites" | "activity";

/** localStorage key: last time this viewer looked at Activity. The bell badge
 *  counts activity events newer than this alongside unread notifications; the
 *  marker resets when the popover opens or the viewer clicks Mark all read. */
const LAST_SEEN_ACTIVITY_KEY = "core.notificationCenter.lastSeenActivityAt";

function formatTimeAgo(iso: string): string {
	const diff = Date.now() - new Date(iso).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

function getDateGroup(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today.getTime() - 86400000);

	if (date >= today) return "Today";
	if (date >= yesterday) return "Yesterday";
	return "Older";
}

function groupByDate(items: Notification[]): Array<{ label: string; items: Notification[] }> {
	const groups: Map<string, Notification[]> = new Map();
	for (const item of items) {
		const label = getDateGroup(item.createdAt);
		if (!groups.has(label)) groups.set(label, []);
		groups.get(label)!.push(item);
	}
	const order = ["Today", "Yesterday", "Older"];
	return order
		.filter((label) => groups.has(label))
		.map((label) => ({ label, items: groups.get(label)! }));
}

// ---------------------------------------------------------------------------
// Notification Item
// ---------------------------------------------------------------------------

function NotificationItem({ n }: { n: Notification }) {
	const { markRead, removeNotification, toggleFavorite } = useNotificationStore();

	return (
		<div
			className={`px-4 py-3 cursor-pointer transition-colors hover:bg-warm-bg group ${
				!n.read ? "bg-teal-light/30" : ""
			}`}
			onClick={() => markRead(n.id)}
		>
			<div className="flex items-start gap-3">
				{/* Status icon */}
				<div className="mt-0.5 shrink-0">
					{n.status === "running" && (
						<Loader2 className="h-4 w-4 text-teal animate-spin" />
					)}
					{n.status === "done" && (
						<Check className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
					)}
					{n.status === "failed" && (
						<X className="h-4 w-4 text-destructive" />
					)}
				</div>

				<div className="flex-1 min-w-0">
					<div className="text-sm font-medium truncate">{n.title}</div>
					<div className="text-xs text-muted-foreground truncate">
						{n.message}
					</div>

					{/* Progress bar for running jobs with known total */}
					{n.status === "running" && n.progress && n.progress.total > 0 && (
						<div className="mt-1.5">
							<div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
								<span>
									{n.progress.completed}/{n.progress.total}
								</span>
								<span>
									{Math.round(
										(n.progress.completed / Math.max(n.progress.total, 1)) *
											100,
									)}
									%
								</span>
							</div>
							<div className="h-1.5 bg-muted rounded-full overflow-hidden">
								<div
									className="h-full bg-teal rounded-full transition-all"
									style={{
										width: `${(n.progress.completed / Math.max(n.progress.total, 1)) * 100}%`,
									}}
								/>
							</div>
						</div>
					)}

					{/* Scanning state — total unknown */}
					{n.status === "running" && (!n.progress || n.progress.total === 0) && (
						<div className="text-xs text-muted-foreground mt-1">
							Scanning pages...
						</div>
					)}

					<div className="text-xs text-muted-foreground mt-1">
						{formatTimeAgo(n.createdAt)}
					</div>
				</div>

				{/* Actions */}
				<div className="flex items-center gap-1 shrink-0">
					{/* Favorite toggle */}
					<button
						className={`transition-colors mt-0.5 ${
							n.favorited
								? "text-amber-400"
								: "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-amber-400"
						}`}
						onClick={(e) => {
							e.stopPropagation();
							toggleFavorite(n.id);
						}}
					>
						<Star className={`h-3.5 w-3.5 ${n.favorited ? "fill-current" : ""}`} />
					</button>

					{/* Dismiss — only for completed/failed */}
					{n.status !== "running" && (
						<button
							className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive mt-0.5"
							onClick={(e) => {
								e.stopPropagation();
								removeNotification(n.id);
							}}
						>
							<X className="h-3.5 w-3.5" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Pinned Ads row
// ---------------------------------------------------------------------------

// ⚠ label's count comes from the caller pre-checked (> 0, or != null && > 0
// for Search Terms) — this component only renders, it does not decide
// whether to render. Keeps the null-vs-0 distinction owned by the caller,
// matching the sidebar badge's own convention.
function PinnedAdsRow({
	count,
	label,
	url,
	onNavigate,
}: {
	count: number;
	label: string;
	url: string;
	onNavigate: (url: string) => void;
}) {
	return (
		<button
			type="button"
			className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm hover:bg-warm-bg transition-colors"
			onClick={() => onNavigate(url)}
		>
			<span className="truncate">
				<span className="font-medium text-coral-dark">{count}</span> pending —{" "}
				{label}
			</span>
			<ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
		</button>
	);
}

// ---------------------------------------------------------------------------
// Date Divider
// ---------------------------------------------------------------------------

function DateDivider({ label }: { label: string }) {
	return (
		<div className="px-4 py-1.5 bg-muted border-b sticky top-0">
			<span className="text-xs font-medium text-muted-foreground">{label}</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Tab Bar
// ---------------------------------------------------------------------------

function TabBar({
	active,
	onChange,
	counts,
}: {
	active: Tab;
	onChange: (tab: Tab) => void;
	counts: { all: number; unread: number; favorites: number; activity: number };
}) {
	const tabs: Array<{ key: Tab; label: string; count: number }> = [
		{ key: "all", label: "All", count: counts.all },
		{ key: "unread", label: "Unread", count: counts.unread },
		{ key: "favorites", label: "Favorites", count: counts.favorites },
		{ key: "activity", label: "Activity", count: counts.activity },
	];

	return (
		<div className="flex border-b">
			{tabs.map((tab) => (
				<button
					key={tab.key}
					className={`flex-1 px-3 py-2 text-xs font-medium transition-colors relative ${
						active === tab.key
							? "text-coral"
							: "text-muted-foreground hover:text-foreground"
					}`}
					onClick={() => onChange(tab.key)}
				>
					{tab.label}
					{tab.count > 0 && (
						<span
							className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
								active === tab.key
									? "bg-coral/10 text-coral"
									: "bg-muted text-muted-foreground"
							}`}
						>
							{tab.count}
						</span>
					)}
					{active === tab.key && (
						<div className="absolute bottom-0 left-2 right-2 h-0.5 bg-coral rounded-t" />
					)}
				</button>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Activity Item — audit trail from planner.activity_events (read-only)
// ---------------------------------------------------------------------------

/**
 * Where a click on this event should navigate. The activity feed shares one
 * shape across every write, so the mapping here is the single place the bell
 * decides "and this one belongs on the client detail page." Returning null
 * means the modal will show detail but no primary action.
 */
function activityDestination(e: ActivityEvent): string | null {
	if (e.client_id) return `/clients/c/${e.client_id}`;
	if (e.entity_type === "client" && e.entity_id) return `/clients/c/${e.entity_id}`;
	return null;
}

/**
 * A pacing/plan write from Core stamps activity_events so "who moved this
 * budget?" has an answer. The bell surfaces the trail here so that answer
 * is one click away from any Core page.
 */
function ActivityRow({ e, onOpen }: { e: ActivityEvent; onOpen: (e: ActivityEvent) => void }) {
	const actor = e.actor_email ?? "system";
	return (
		<button
			type="button"
			className="block w-full px-4 py-3 border-b text-left hover:bg-warm-bg focus:bg-warm-bg focus:outline-none"
			onClick={() => onOpen(e)}
		>
			<div className="flex items-start gap-3">
				<Activity className="mt-0.5 h-4 w-4 shrink-0 text-teal" aria-hidden />
				<div className="min-w-0 flex-1">
					<div className="text-sm font-medium leading-snug">{e.title}</div>
					{e.message ? (
						<div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
							{e.message}
						</div>
					) : null}
					<div className="mt-1 text-xs text-muted-foreground">
						<span className="font-medium">{actor}</span>
						<span className="mx-1">·</span>
						{formatTimeAgo(e.created_at)}
					</div>
				</div>
				<ChevronRight className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
			</div>
		</button>
	);
}

/**
 * Full-detail view of one activity event. Renders every field the bell
 * doesn't have room for (metadata, actor, entity ids) and offers a primary
 * "Open" action to whichever page owns the referenced entity.
 */
function ActivityDetailModal({
	event,
	onOpenChange,
	onNavigate,
}: {
	event: ActivityEvent | null;
	onOpenChange: (open: boolean) => void;
	onNavigate: (url: string) => void;
}) {
	if (!event) return null;
	const dest = activityDestination(event);
	const actor = event.actor_email ?? "system";
	const when = new Date(event.created_at);
	const metadata = event.metadata ?? {};
	const metaEntries = Object.entries(metadata).filter(
		([k]) => !["source", "confirmed_by", "reason", "trigger"].includes(k),
	);
	return (
		<Dialog open onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="pr-6">{event.title}</DialogTitle>
				</DialogHeader>
				<div className="min-h-0 flex-1 overflow-y-auto space-y-3 text-sm">
					{event.message ? (
						<p className="whitespace-pre-wrap text-muted-foreground">{event.message}</p>
					) : null}
					<dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
						<dt className="text-muted-foreground">Actor</dt>
						<dd className="font-medium">{actor}</dd>
						<dt className="text-muted-foreground">When</dt>
						<dd>{when.toLocaleString()}<span className="ml-2 text-muted-foreground">({formatTimeAgo(event.created_at)})</span></dd>
						<dt className="text-muted-foreground">Kind</dt>
						<dd className="font-mono">{event.kind}</dd>
						{event.entity_type ? (
							<>
								<dt className="text-muted-foreground">Entity</dt>
								<dd className="font-mono break-all">{event.entity_type}{event.entity_id ? " · " + event.entity_id : ""}</dd>
							</>
						) : null}
						{metadata.confirmed_by ? (
							<>
								<dt className="text-muted-foreground">Confirmed by</dt>
								<dd>{String(metadata.confirmed_by)}</dd>
							</>
						) : null}
						{metadata.reason ? (
							<>
								<dt className="text-muted-foreground">Reason</dt>
								<dd className="whitespace-pre-wrap">{String(metadata.reason)}</dd>
							</>
						) : null}
					</dl>
					{metaEntries.length > 0 ? (
						<details className="rounded border border-border bg-warm-bg/40 p-2">
							<summary className="cursor-pointer text-xs text-muted-foreground">
								Details ({metaEntries.length})
							</summary>
							<pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug">
								{JSON.stringify(Object.fromEntries(metaEntries), null, 2)}
							</pre>
						</details>
					) : null}
				</div>
				<DialogFooter className="gap-2 sm:justify-between">
					<Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
						Close
					</Button>
					{dest ? (
						<Button size="sm" onClick={() => { onNavigate(dest); onOpenChange(false); }}>
							<ExternalLink className="mr-1.5 h-3.5 w-3.5" />
							Open client
						</Button>
					) : null}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function groupActivityByDate(items: ActivityEvent[]): Array<{ label: string; items: ActivityEvent[] }> {
	const groups: Map<string, ActivityEvent[]> = new Map();
	for (const item of items) {
		const label = getDateGroup(item.created_at);
		if (!groups.has(label)) groups.set(label, []);
		groups.get(label)!.push(item);
	}
	const order = ["Today", "Yesterday", "Older"];
	return order.filter((l) => groups.has(l)).map((l) => ({ label: l, items: groups.get(l)! }));
}

/** A row in the merged "All" list — notifications and activity events shown
 *  together, ordered by timestamp, grouped by date. */
type MergedItem =
	| { kind: "notif"; createdAt: string; n: Notification }
	| { kind: "activity"; createdAt: string; e: ActivityEvent };

function groupMergedByDate(
	notifs: Notification[],
	events: ActivityEvent[],
): Array<{ label: string; items: MergedItem[] }> {
	const merged: MergedItem[] = [
		...notifs.map((n) => ({ kind: "notif" as const, createdAt: n.createdAt, n })),
		...events.map((e) => ({ kind: "activity" as const, createdAt: e.created_at, e })),
	].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
	const groups: Map<string, MergedItem[]> = new Map();
	for (const m of merged) {
		const label = getDateGroup(m.createdAt);
		if (!groups.has(label)) groups.set(label, []);
		groups.get(label)!.push(m);
	}
	const order = ["Today", "Yesterday", "Older"];
	return order.filter((l) => groups.has(l)).map((l) => ({ label: l, items: groups.get(l)! }));
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function NotificationCenter({
	adsManagementCount,
	adsSearchTermsCount,
}: {
	/** Management "Easy Yes/No" pending count. Same source as the sidebar
	 *  badge (operatorQueueQuery). Absent/0/undefined → no pinned row, no
	 *  presence dot from this source. */
	adsManagementCount?: number;
	/** Search Terms unapplied-easy-rec count. `null` means "no snapshot
	 *  yet" (never a fake 0) — matches the sidebar badge's own contract.
	 *  Only ever checked as `!= null && > 0`; never coalesced, since this
	 *  component no longer folds it into a number at all (see the presence
	 *  dot below) — there is nothing left that a coalesced 0 could corrupt,
	 *  but the distinction is kept because "no snapshot" and "genuinely
	 *  zero" are different facts even for a boolean presence check. */
	adsSearchTermsCount?: number | null;
} = {}) {
	const [tab, setTab] = useState<Tab>("all");
	const [open, setOpen] = useState(false);
	const [selectedActivity, setSelectedActivity] = useState<ActivityEvent | null>(null);
	const navigate = useNavigate();
	const openActivity = (e: ActivityEvent) => {
		setOpen(false);        // close popover; modal takes over
		setSelectedActivity(e);
	};
	const { notifications, markAllRead, removeNotification, unreadCount } =
		useNotificationStore();

	// Persisted per-browser marker: activity events created after this are
	// "unread" and count toward the bell badge, matching how Notifications
	// count `read: false` items.
	const [lastSeenActivityAt, setLastSeenActivityAt] = useState<string>(() => {
		try {
			return localStorage.getItem(LAST_SEEN_ACTIVITY_KEY) ?? "1970-01-01T00:00:00Z";
		} catch {
			return "1970-01-01T00:00:00Z";
		}
	});

	const jobsUnread = unreadCount();
	const favoriteCount = notifications.filter((n) => n.favorited).length;

	const showManagementRow = Boolean(adsManagementCount && adsManagementCount > 0);
	const showSearchTermsRow =
		adsSearchTermsCount != null && adsSearchTermsCount > 0;
	// Doubles as the pinned-rows-section flag and the bell's presence dot —
	// presence only, not a count. The number lives in the pinned rows below
	// and in the sidebar badges; a dot never overflows.
	const showAdsSection = showManagementRow || showSearchTermsRow;

	const handleAdsNavigate = (url: string) => {
		setOpen(false);
		navigate(url);
	};

	// Filter by active tab
	const filtered =
		tab === "unread"
			? notifications.filter((n) => !n.read)
			: tab === "favorites"
				? notifications.filter((n) => n.favorited)
				: notifications;

	// Group by date
	const groups = groupByDate(filtered);

	// Activity feed — always fetched (not just when the Activity tab is
	// open) so the bell badge can count unread activity like it counts
	// unread notifications. Refetches every 60s; one 30-row GET.
	const activityQuery = useQuery({
		queryKey: ["planner-activity-recent"],
		queryFn: async () => {
			const { data } = await apiClient.get<{ events: ActivityEvent[] }>(
				"/planner/activity/recent",
				{ params: { limit: 30 } },
			);
			return data.events;
		},
		staleTime: 60_000,
		refetchInterval: 60_000,
	});
	const activityEvents = activityQuery.data ?? [];
	const activityGroups = groupActivityByDate(activityEvents);
	const unreadActivity = activityEvents.filter((e) => e.created_at > lastSeenActivityAt);

	// Bell badge = unread jobs + unread activity, so nothing hides in a
	// tab the viewer never opens.
	const count = jobsUnread + unreadActivity.length;
	const countLabel = count > 9 ? "9+" : String(count);

	// Mark activity as seen shortly after the popover opens, matching how
	// jobs unread stays visible until an explicit click. 800ms lets the
	// viewer see the count first.
	useEffect(() => {
		if (!open) return;
		const t = window.setTimeout(() => {
			const now = new Date().toISOString();
			setLastSeenActivityAt(now);
			try { localStorage.setItem(LAST_SEEN_ACTIVITY_KEY, now); } catch { /* private mode */ }
		}, 800);
		return () => window.clearTimeout(t);
	}, [open]);

	const clearFinished = () => {
		notifications
			.filter((n) => n.status !== "running")
			.forEach((n) => removeNotification(n.id));
	};
	const hasFinished = notifications.some((n) => n.status !== "running");

	return (
		<>
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				{/* shrink-0: an icon button never gives up width when the top bar
				    is crowded; the badge below overhangs the box on purpose (the
				    top bar no longer clips or scrolls it — CORE-G-16.b). */}
				<Button variant="ghost" size="icon" className="relative shrink-0">
					<Bell className="h-5 w-5" />
					{count > 0 && (
						<span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-coral px-1 text-xs text-white">
							{countLabel}
						</span>
					)}
					{/* Ads queue presence — distinct from unread jobs above, never
					    cleared by Mark all read (nothing to mark; it clears the
					    same way the sidebar badge does, by resolving the item). */}
					{showAdsSection && (
						<span
							role="status"
							className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-coral ring-2 ring-background"
							aria-label="Ads items pending"
						/>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-96 p-0" align="end">
				{/* Header */}
				<div className="flex items-center justify-between px-4 py-3 border-b">
					<span className="text-sm font-semibold">Notifications</span>
					<div className="flex items-center gap-3">
						{count > 0 && (
							<button
								className="text-xs text-muted-foreground hover:text-coral transition-colors"
								onClick={() => {
									markAllRead();
									const now = new Date().toISOString();
									setLastSeenActivityAt(now);
									try { localStorage.setItem(LAST_SEEN_ACTIVITY_KEY, now); } catch { /* private mode */ }
								}}
							>
								Mark all read
							</button>
						)}
						{hasFinished && (
							<button
								className="text-xs text-muted-foreground hover:text-destructive transition-colors"
								onClick={clearFinished}
							>
								Clear completed
							</button>
						)}
					</div>
				</div>

				{/* Pinned Ads rows — persisted queue counts, not jobs. Never
				    enter useNotificationStore; rendered straight from props so
				    there is no dedupe-across-polls or fake status to get wrong. */}
				{showAdsSection && (
					<div className="divide-y divide-border border-b">
						{showManagementRow && (
							<PinnedAdsRow
								count={adsManagementCount as number}
								label="Management"
								url={ADS_MANAGEMENT_URL}
								onNavigate={handleAdsNavigate}
							/>
						)}
						{showSearchTermsRow && (
							<PinnedAdsRow
								count={adsSearchTermsCount as number}
								label="Search Terms & Negatives"
								url={ADS_SEARCH_TERMS_URL}
								onNavigate={handleAdsNavigate}
							/>
						)}
					</div>
				)}

				{/* Tabs */}
				<TabBar
					active={tab}
					onChange={setTab}
					counts={{
						all: notifications.length + activityEvents.length,
						unread: jobsUnread + unreadActivity.length,
						favorites: favoriteCount,
						activity: unreadActivity.length,
					}}
				/>

				{/* List */}
				<ScrollArea className="max-h-80">
					{tab === "activity" ? (
						activityQuery.isPending ? (
							<div className="px-4 py-8 text-center text-sm text-muted-foreground">
								<Loader2 className="mx-auto h-5 w-5 animate-spin" />
							</div>
						) : activityQuery.isError ? (
							<div className="px-4 py-8 text-center text-sm text-destructive">
								Couldn't load activity.
							</div>
						) : activityEvents.length === 0 ? (
							<div className="px-4 py-8 text-center text-sm text-muted-foreground">
								No recent activity
							</div>
						) : (
							activityGroups.map((group) => (
								<div key={group.label}>
									<DateDivider label={group.label} />
									{group.items.map((e) => (
										<ActivityRow key={e.id} e={e} onOpen={openActivity} />
									))}
								</div>
							))
						)
					) : tab === "all" ? (
						/* Interleave notifications and activity in one date-ordered
						 * list so nothing hides in a tab the viewer never opens. */
						(() => {
							const merged = groupMergedByDate(notifications, activityEvents);
							if (merged.length === 0) {
								return (
									<div className="px-4 py-8 text-center text-sm text-muted-foreground">
										Nothing yet
									</div>
								);
							}
							return merged.map((group) => (
								<div key={group.label}>
									<DateDivider label={group.label} />
									{group.items.map((m) =>
										m.kind === "notif" ? (
											<NotificationItem key={"n-" + m.n.id} n={m.n} />
										) : (
											<ActivityRow key={"a-" + m.e.id} e={m.e} onOpen={openActivity} />
										),
									)}
								</div>
							));
						})()
					) : filtered.length === 0 ? (
						<div className="px-4 py-8 text-center text-sm text-muted-foreground">
							{tab === "unread"
								? "All caught up!"
								: tab === "favorites"
									? "No favorites yet"
									: "No notifications"}
						</div>
					) : (
						groups.map((group) => (
							<div key={group.label}>
								<DateDivider label={group.label} />
								{group.items.map((n) => (
									<NotificationItem key={n.id} n={n} />
								))}
							</div>
						))
					)}
				</ScrollArea>
			</PopoverContent>
		</Popover>
		<ActivityDetailModal
			event={selectedActivity}
			onOpenChange={(o) => { if (!o) setSelectedActivity(null); }}
			onNavigate={navigate}
		/>
		</>
	);
}
